import { useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ASSETS } from "@/lib/constants";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Download, ArrowLeft } from "lucide-react";
import html2canvas from "html2canvas";

export default function Certificate() {
  const { user } = useAuth();
  const ref = useRef(null);

  if (!user) return null;

  const download = async () => {
    if (!ref.current) return;
    const canvas = await html2canvas(ref.current, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
    const link = document.createElement("a");
    link.download = `iBAG-Membership-${user.member_id}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const today = new Date().toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 sm:px-12 py-12">
        <div className="flex items-center justify-between mb-8">
          <Link to="/dashboard" className="flex items-center gap-2 text-sm text-[#A8A29E] hover:text-[#F5F3EF]" data-testid="cert-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
          <Button onClick={download} className="rounded-none bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#C8102E] hover:text-white" data-testid="cert-download-btn">
            <Download className="w-4 h-4 mr-2" /> Download Certificate
          </Button>
        </div>

        {/* Certificate */}
        <div ref={ref} className="bg-white p-3" data-testid="certificate">
          <div className="border-[3px] border-[#D4AF37] p-1">
            <div className="border border-[#0A0A0A] p-10 sm:p-14 relative bg-white text-[#1a1a1a]">
              <div className="tribal-stripe h-2 w-full absolute top-0 left-0" />

              <div className="flex items-center justify-between mt-4">
                <img src={ASSETS.flag} alt="Flag" crossOrigin="anonymous" className="h-24 w-24 object-cover border border-[#0A0A0A]/20" />
                <div className="text-center px-4">
                  <div className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#8B0000] font-semibold">Indigenous Black American Tribal Nation</div>
                  <div className="font-serif-display text-2xl sm:text-3xl font-semibold mt-1 text-[#0A0A0A]">The Black American Government</div>
                </div>
                <img src={ASSETS.seal} alt="Seal" crossOrigin="anonymous" className="h-24 w-24 object-contain" />
              </div>

              <div className="text-center mt-10">
                <div className="font-serif-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A0A0A]">Certificate of Membership</div>
                <p className="text-sm text-[#333333] mt-3 max-w-xl mx-auto">This certifies that the individual named below is a duly enrolled member of the Nation, having freely affirmed the Preamble and signed the Declaration of Membership.</p>
              </div>

              <div className="text-center my-10">
                <div className="font-serif-display text-5xl sm:text-6xl italic text-[#8B0000]">{user.full_name}</div>
                <div className="h-0.5 w-2/3 mx-auto mt-4 bg-[#8B0000]" />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 text-center mb-10">
                <Detail label="Member No." value={user.member_id} />
                <Detail label="Region" value={user.region || "—"} />
                <Detail label="Date of Enrollment" value={today} />
                <Detail label="Location of Birth" value={user.place_of_birth || "—"} />
                <Detail label="Residence" value={user.residence || "—"} />
                <Detail label="Skills of Service" value={`${(user.skills || []).length} recorded`} />
              </div>

              <div className="flex items-end justify-between mt-12">
                <div className="text-center">
                  <div className="font-serif-display text-2xl italic border-b border-[#0A0A0A] px-8 pb-1 text-[#0A0A0A]">{user.signature_name || user.full_name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#333333] mt-2">Member Signature</div>
                </div>
                <div className="text-center">
                  <div className="font-serif-display text-2xl italic border-b border-[#0A0A0A] px-8 pb-1 text-[#0A0A0A]">Chief Facilitator</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#333333] mt-2">Office of the Chief Facilitator</div>
                </div>
              </div>

              <p className="text-center text-[10px] tracking-[0.2em] uppercase text-[#8B0000] font-semibold mt-12">
                Unity &middot; Self-Determination &middot; Service
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#8B0000] font-semibold">{label}</div>
      <div className="font-medium text-sm mt-1 text-[#0A0A0A]">{value}</div>
    </div>
  );
}
